import { flow } from "mobx";
import { Instance, types } from "mobx-state-tree";
import { AgentGroup as ApiAgentGroup } from "@closerplatform/spinner-openapi";
import { currentUserStoreGetter, servicesGetters, uiStoreGetter } from "../utils";
import { AgentGroup, AgentGroupModel, IAgentGroup } from "./agentGroup";
import { logger } from "@fidget/common/utils";
import { notEmpty } from "@fidget/common/utils/not-empty";
import { AgentGroupsEventListener } from "./eventHandlers";

export const AgentGroupStore = types
  .model("AgentGroupStore", {
    groupsMap: types.map(AgentGroup)
  })
  .views(servicesGetters)
  .views(currentUserStoreGetter)
  .views(uiStoreGetter)
  .views((self) => {
    const views = {
      get groups(): IAgentGroup[] {
        return [...self.groupsMap.values()];
      },

      getById(id: string): IAgentGroup | undefined {
        return self.groupsMap.get(id);
      },

      getByIds(ids: string[]): IAgentGroup[] {
        return ids.map((id) => self.groupsMap.get(id)).filter(notEmpty);
      },

      getDTOById(id: string): ApiAgentGroup | undefined {
        const group = self.groupsMap.get(id);
        const { orgId } = self.currentUserStore;
        if (!group || !orgId) {
          return;
        }

        return {
          orgId,
          id: group.id,
          name: group.name,
          tags: group.tagIds.toJS(),
          tagGroups: group.tagGroupIds.toJS(),
          advisers: group.adviserIds.toJS()
        };
      }
    };

    return views;
  })
  .actions((self) => ({
    updateGroup(group: ApiAgentGroup) {
      self.groupsMap.set(group.id, AgentGroupModel.fromApiDTO(group));
    },

    deleteGroup(groupId: string) {
      self.groupsMap.delete(groupId);
    },

    setGroups(groups: ApiAgentGroup[]) {
      self.groupsMap.clear();
      groups.forEach((group) => self.groupsMap.put(AgentGroupModel.fromApiDTO(group)));
    }
  }))
  .actions((self) => ({
    load: flow(function* load() {
      try {
        const groups: ApiAgentGroup[] = yield self.spinnerService.getAgentGroups();
        self.setGroups(groups);

        return groups;
      } catch (e) {
        logger.error({ message: "Couldn't load agent groups", error: e });
        self.uiStore.addGenericErrorToast();

        return [];
      }
    }),
    afterCreate() {
      self.eventListener.addGlobalListener(
        new AgentGroupsEventListener(() => self as IAgentGroupStore, "AgentGroupsEventListener")
      );
    },
  }));

export interface IAgentGroupStore extends Instance<typeof AgentGroupStore> {}
